import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, Globe } from "lucide-react";
import { getContactData } from "../../api/contactApi"; 
import { useSettings } from "../../context/SettingsContext";

const Footer = ({ showBackToTop }) => {
  const { 
    logoFooterUrl,
    contactEmail, 
    officialWebsite, 
    address, 
    phone, 
    companyName 
  } = useSettings();

  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getContactData().then((data) => {
      setContact(data);
      setLoading(false);
    });
  }, []);

  if (loading || !contact) return null;

  // Fallback Logic
  const displayEmail = contactEmail || "Alamat email belum diatur dalam sistem";
  const displayWebsite = officialWebsite || "Website belum diatur dalam sistem";

  return (
  <footer className="bg-[#E7E8EA] text-[#064180] pt-20 pb-10 font-sans mt-20 transition-colors duration-500 border-t border-white/5 relative overflow-hidden">
    {/* Background Glow Effect */}
    <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 blur-[100px] -ml-32 -mb-32"></div> 
    {/* Main Content Grid */}
    <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 mb-16 relative z-10">   
      {/* Kolom 1: Connect Info */}
      <div className="space-y-4">
        <h4 className="text-[#064180] font-bold text-xs mb-6 uppercase tracking-widest">Connect</h4>
        
        <a href={`mailto:${displayEmail}`} className="flex items-center gap-3 group p-2 -ml-2 rounded-xl transition-all hover:bg-white/5 w-fit">
          <div className="w-8 h-8 bg-[#00df9a]/10 rounded-lg flex items-center justify-center text-[#064180]  group-hover:text-[#0F172A] transition-all">
            <Mail size={16} />
          </div>
          <div className="flex flex-col">
            <span className="text-[9px] text-[#064180] font-black uppercase leading-none mb-1">Official Email</span>
            <span className="font-bold text-xs text-[#064180] transition-colors">{displayEmail}</span>
          </div>
        </a>

        <a href={`https://${displayWebsite}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 group p-2 -ml-2 rounded-xl transition-all hover:bg-white/5 w-fit">
          <div className="w-8 h-8 bg-[#00df9a]/10 rounded-lg flex items-center justify-center text-[#064180] group-hover:text-[#0F172A] transition-all">
            <Globe size={16} />
          </div>
          <div className="flex flex-col">
            <span className="text-[9px] text-[#064180] font-black uppercase leading-none mb-1">Official Website</span>
            <span className="font-bold text-xs text-[#064180] transition-colors">{displayWebsite}</span>
          </div>
        </a>
      </div>

      {/* Kolom 2: Location */}
      <div className="lg:col-span-1">
        <h4 className="text-[#064180] font-bold text-xs mb-6 uppercase tracking-widest">Location</h4>
        <div className="space-y-4 text-[#064180]">
          <div className="flex flex-col gap-1">
            <span className="text-[#064180] font-bold text-sm tracking-tight">
              {companyName || "Siloam International Hospitals"}
            </span>
            <p className="text-xs leading-relaxed text-[#064180]">
              {address || "Alamat belum diatur dalam sistem"}
            </p>
          </div>

          <div className="flex flex-col gap-1 border-t border-white/5 pt-3">
            <span className="text-[10px] text-[#064180] font-black uppercase tracking-wider">Contact Person</span>
            <p className="text-xs font-medium text-[#064180]">
              {phone ? `${phone} (Admin)` : "Nomor belum tersedia"}
            </p>
          </div>
        </div>
      </div>

      {/* Kolom 3: Brand Logo / Placeholder */}
      <div className="flex flex-col items-start lg:items-end justify-start h-full">
        <h4 className="text-[#064180] font-bold text-sm mb-6 uppercase tracking-widest lg:hidden">
          Social
        </h4>
        
        <div className="group relative">
          {logoFooterUrl ? (
            /* Menampilkan Logo jika ada */
            <img 
              src={logoFooterUrl} 
              alt="Brand Logo" 
              className="h-24 xl:h-32 w-auto object-contain transition-all group-hover:scale-105" 
            />
          ) : (
            /* Placeholder Beauty Code jika logo kosong */
            <div className="flex flex-col items-start lg:items-end justify-center py-4 transition-all duration-300">
              <span className="text-[14px] xl:text-[16px] font-black tracking-[0.2em] text-[#064180] uppercase leading-none mb-1.5 text-left lg:text-right">
                Pasang Logo
              </span>
              <span className="text-[10px] xl:text-[11px] font-medium tracking-[0.3em] text-[#064180] uppercase leading-none opacity-80 text-left lg:text-right">
                Anda Disini
              </span>
              {/* Garis Aksen Dinamis */}
              <div className="mt-4 w-12 h-[2px] bg-[#064180]/20 group-hover:w-16 group-hover:bg-[#064180] transition-all duration-500" />
            </div>
          )}
        </div>
      </div>
    </div>

   {/* Bottom Bar */}
    <div className="border-t border-white/10 pt-10 pb-10 px-6 relative z-10">
      <div className="max-w-7xl mx-auto flex justify-center items-center">
        {/* Mengubah text-white/30 menjadi text-white agar berwarna putih terang */}
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-center text-[#064180]">
          © {new Date().getFullYear()} {displayWebsite}
        </p>
      </div>
      
      {/* Back to Top Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: showBackToTop ? 1 : 0, scale: showBackToTop ? 1 : 0 }}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="fixed bottom-10 right-6 md:right-10 z-[100] w-12 h-12 flex items-center justify-center bg-[#00df9a] text-[#0F172A] rounded-2xl shadow-[0_0_25px_rgba(0,223,154,0.4)] transition-all cursor-pointer border-none"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </motion.button>
    </div>
  </footer>
  );
};

export default Footer;